import { useEffect, useRef } from "react";

/**
 * Keyboard shortcuts for the control room. The operator drives a live
 * broadcast one-handed — every saved click matters — so the fast actions
 * (timer toggle, quick events, hide overlay) get single-key bindings.
 *
 * Bindings are written human-readable and normalized internally:
 *   "space", "g", "esc", "mod+enter", "shift+/"
 * where `mod` = Cmd on macOS, Ctrl elsewhere. Modifier order doesn't matter.
 *
 *   useHotkeys({
 *     space: () => toggleTimer(),
 *     g: () => openGoalDialog(),
 *     esc: () => hideOverlay(),
 *   });
 *
 * Keystrokes are ignored while the operator types in an input / textarea /
 * contenteditable (unless `allowInInputs` is set), so shortcuts never eat
 * real typing.
 */
export type HotkeyHandler = (e: KeyboardEvent) => void;
export type HotkeyMap = Record<string, HotkeyHandler>;

export type UseHotkeysOptions = {
  /** Disable the whole map without unmounting (e.g. while a modal owns keys). */
  enabled?: boolean;
  /** Fire even when focus is inside a text field. Default: false. */
  allowInInputs?: boolean;
};

const EDITABLE_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"]);

function isEditableTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el.tagName !== "string") return false;
  if (EDITABLE_TAGS.has(el.tagName)) return true;
  return el.isContentEditable === true;
}

/** Canonical token for a live keypress — modifiers first in a fixed order,
 *  then the lowercased key with a few friendly aliases. */
function eventToken(e: KeyboardEvent): string {
  const parts: string[] = [];
  if (e.ctrlKey || e.metaKey) parts.push("mod");
  if (e.altKey) parts.push("alt");
  if (e.shiftKey) parts.push("shift");
  parts.push(canonicalKey(e.key.toLowerCase()));
  return parts.join("+");
}

/** Same canonicalization applied to a user-written binding string. */
function normalizeBinding(binding: string): string {
  let mod = false;
  let alt = false;
  let shift = false;
  let key = "";
  for (const rawPart of binding.toLowerCase().split("+")) {
    const part = rawPart.trim();
    if (!part) continue;
    if (
      part === "mod" ||
      part === "cmd" ||
      part === "ctrl" ||
      part === "control" ||
      part === "meta"
    ) {
      mod = true;
    } else if (part === "alt" || part === "option") {
      alt = true;
    } else if (part === "shift") {
      shift = true;
    } else {
      key = canonicalKey(part);
    }
  }
  const parts: string[] = [];
  if (mod) parts.push("mod");
  if (alt) parts.push("alt");
  if (shift) parts.push("shift");
  parts.push(key);
  return parts.join("+");
}

function canonicalKey(key: string): string {
  switch (key) {
    case " ":
    case "spacebar":
      return "space";
    case "escape":
      return "esc";
    case "arrowup":
      return "up";
    case "arrowdown":
      return "down";
    case "arrowleft":
      return "left";
    case "arrowright":
      return "right";
    default:
      return key;
  }
}

export function useHotkeys(map: HotkeyMap, options: UseHotkeysOptions = {}): void {
  const { enabled = true, allowInInputs = false } = options;

  // Hold the latest map in a ref so callers can pass a fresh object literal
  // every render without re-binding the listener each time.
  const mapRef = useRef(map);
  mapRef.current = map;

  useEffect(() => {
    if (!enabled) return;

    function onKeyDown(e: KeyboardEvent): void {
      if (!allowInInputs && isEditableTarget(e.target)) return;
      const token = eventToken(e);
      const current = mapRef.current;
      for (const binding in current) {
        const handler = current[binding];
        if (handler && normalizeBinding(binding) === token) {
          e.preventDefault();
          handler(e);
          return;
        }
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled, allowInInputs]);
}
